/**
 * Share payload + storage types.
 *
 * A shared chat is uploaded to the proxy-share store (`/s/api`). It can be:
 *  - a full transcript, a user-selected subset, or a single message block; and
 *  - optionally PIN-protected, in which case only an encrypted envelope is
 *    stored — a public GET reveals nothing without the PIN.
 */
import type { StoredSession } from '../types/session.ts';

/** How the viewer should present a shared session. */
export type ShareMode = 'full' | 'selection' | 'single';

/**
 * A file the transcript points at, carried inside the share.
 *
 * A transcript refers to pictures and HTML artefacts by absolute path, which
 * means something only on the machine that made them. A reader has no such
 * machine, so anything not carried here simply is not there.
 */
export interface SharedFile {
  /** MIME type, used to build the data URL the viewer renders. */
  mime: string;
  /** base64 file contents. */
  data: string;
}

/**
 * How a share from the canvas should be presented.
 *
 * The transcript is the substance and travels as a transcript. This is the part
 * a transcript cannot hold: that it came from a canvas at all, what the thing
 * was called, and what fed it. A reader given an answer without the answers it
 * was built on has been handed a conclusion with its reasoning removed.
 *
 * Presentation only, and optional throughout — a reader that knows nothing
 * about canvases ignores it and still shows the whole conversation.
 */
export interface AirSharePresentation {
  /** One exchange, or a thread of them. */
  kind: 'node' | 'conversation';
  /** What it was called on the canvas, if it was named. */
  title?: string;
  /** The emoji standing at the head of it. */
  icon?: string;
  /**
   * The shared nodes laid out as they sit on the canvas, so a reader can see the
   * arrangement the author built — where each node is, what connects to what —
   * rather than only its flattening into a transcript. Only the shared nodes
   * travel, and the viewer renders them read-only. Optional and additive: a
   * share made before this simply has no canvas, and the viewer falls back to
   * the transcript.
   */
  canvas?: AirShareCanvas;
  /**
   * The picture across the top, named by the same path the file map is keyed
   * by. Absent from `files` means it did not travel — the header simply has no
   * picture, which is how it looks for everything that never had one.
   */
  cover?: string;
  /**
   * What fed it: the questions of the blocks upstream, in the order the run
   * read them. Empty means nothing fed it — which is worth saying, because it
   * is the difference between missing context and none.
   */
  inputs?: string[];
}

/**
 * One shared node in canvas coordinates, carrying just enough to draw it: where
 * it sits, how big it is, its question and its answer. The answer is markdown,
 * and any pictures it names live in the share's `files` map by the same path —
 * so nothing is duplicated.
 */
export interface SharedCanvasNode {
  id: string;
  /** Canvas position (top-left), in the same units the author arranged. */
  x: number;
  y: number;
  /** Chosen size, if the node was resized. */
  width?: number;
  height?: number;
  /** The prompt, shown above the answer as it is on the card. */
  prompt: string;
  /** The answer markdown, if the node produced one. */
  output?: string;
  /** Its place in a chain — "1", "1.2" — if it is in one. */
  number?: string;
  /** A bare node or a chat message, so the viewer can style it accordingly. */
  kind?: 'node' | 'chat';
}

/** The shared subgraph: the nodes and the edges among them. */
export interface AirShareCanvas {
  nodes: SharedCanvasNode[];
  /** Connections between shared nodes; endpoints reference node ids. */
  edges: { from: string; to: string }[];
}

/**
 * The plaintext share payload (v1). For `selection`/`single`, `session` is
 * already filtered to only the chosen messages before it is ever serialized —
 * non-selected content is never uploaded. `mode` drives viewer presentation.
 */
export interface SharePayloadV1 {
  v: 1;
  mode: ShareMode;
  session: StoredSession;
  /**
   * Present when this came from the canvas rather than from a chat. Absent for
   * every chat share, which is what keeps the chat viewer unchanged.
   */
  air?: AirSharePresentation;
  /**
   * Files the transcript refers to, keyed by the path it refers to them by.
   * Absent on shares made before this existed — the viewer must cope.
   */
  files?: Record<string, SharedFile>;
  /**
   * Paths that were referred to but left out, because they were too big or
   * unreadable. Stated rather than dropped: a reader should learn that
   * something is missing from the page, not from a gap where it should be.
   */
  filesOmitted?: string[];
}

/**
 * AES-256-GCM + PBKDF2(SHA-256) envelope for a PIN-encrypted share. This is the
 * only thing stored server-side for a protected share, so a public GET cannot
 * reveal the transcript without the PIN. All binary fields are base64.
 */
export interface EncryptedShareEnvelope {
  enc: 'aes-256-gcm';
  kdf: 'pbkdf2-sha256';
  iterations: number;
  /** base64 PBKDF2 salt */
  salt: string;
  /** base64 AES-GCM iv */
  iv: string;
  /** base64 ciphertext (includes the GCM auth tag) */
  ciphertext: string;
}

/**
 * What proxy-share stores under an id. Either a plaintext payload (no PIN) or an
 * encrypted envelope (PIN-protected, `protected: true` so the viewer prompts).
 * The viewer also tolerates a legacy bare `StoredSession` (pre-`v` shares).
 */
export type StoredShareBlob =
  | ({ protected?: false } & SharePayloadV1)
  | { protected: true; envelope: EncryptedShareEnvelope };

/** Type guard: is this stored blob a PIN-protected (encrypted) share? */
export function isEncryptedShare(
  blob: unknown,
): blob is { protected: true; envelope: EncryptedShareEnvelope } {
  return (
    typeof blob === 'object' &&
    blob !== null &&
    (blob as { protected?: unknown }).protected === true &&
    typeof (blob as { envelope?: unknown }).envelope === 'object'
  );
}
